import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  ExternalAuditAttachment,
  ExternalAuditReport,
  ExternalAuditReportPayload,
} from '../types/externalAudit';

export async function fetchExternalAudits(
  keyword?: string,
  contractId?: number | null
): Promise<ExternalAuditReport[]> {
  const params: Record<string, string | number> = {};
  if (keyword?.trim()) params.q = keyword.trim();
  if (contractId != null) params.contractId = contractId;
  const { data } = await apiClient.get<ExternalAuditReport[]>('/external-audits', {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function fetchExternalAudit(reportId: number): Promise<ExternalAuditReport> {
  const { data } = await apiClient.get<ExternalAuditReport>(`/external-audits/${reportId}`);
  return data;
}

export async function createExternalAudit(
  payload: ExternalAuditReportPayload
): Promise<ExternalAuditReport> {
  const { data } = await apiClient.post<ExternalAuditReport>('/external-audits', payload);
  return data;
}

export async function updateExternalAudit(
  reportId: number,
  payload: ExternalAuditReportPayload
): Promise<ExternalAuditReport> {
  const { data } = await apiClient.patch<ExternalAuditReport>(
    `/external-audits/${reportId}`,
    payload
  );
  return data;
}

export async function deleteExternalAudit(reportId: number): Promise<void> {
  await apiClient.delete(`/external-audits/${reportId}`);
}

export async function approveExternalAudit(reportId: number): Promise<ExternalAuditReport> {
  const { data } = await apiClient.post<ExternalAuditReport>(`/external-audits/${reportId}/approve`);
  return data;
}

export async function revokeExternalAudit(reportId: number): Promise<ExternalAuditReport> {
  const { data } = await apiClient.post<ExternalAuditReport>(`/external-audits/${reportId}/revoke`);
  return data;
}

export async function uploadExternalAuditAttachments(
  reportId: number,
  files: File[]
): Promise<ExternalAuditAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<ExternalAuditAttachment[]>(
    `/external-audits/${reportId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteExternalAuditAttachment(
  reportId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/external-audits/${reportId}/attachments/${attachmentId}`);
}

export async function downloadExternalAuditAttachment(
  reportId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/external-audits/${reportId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export async function downloadExternalAuditPdf(
  reportId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/external-audits/${reportId}/pdf`,
    fileName || `외부심사보고서_${reportId}.pdf`
  );
}
